import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { fetchMonitorDashboardData, postColumnVisibility } from "./api";
import { buildMonitorColumns } from "./columns";
import CustomizeColumnsDialog from "./CustomizeColumnsDialog";
import FilterBar from "./FilterBar";
import Grid from "./Grid";
import Pagination from "./Pagination";
import PatientDetailsPanel from "./PatientDetailsPanel";
import QuickSearchBar from "./QuickSearchBar";
import { buildDashboardParams, buildFilterOptions, buildPatientStatusOptions, defaultQueryState, loadQueryState, loadSelection, saveQueryState, saveSelection } from "./state";
import { MonitorAppProps, MonitorCounts, MonitorMeta, MonitorQueryState, MonitorRow, SortOrder } from "./types";
import {
  publishSelectedIds,
  publishReloadCallback,
  publishLockPatcher,
  publishFilterChangeHandlers,
  releaseLocks,
  setNavigationMenu,
  enablePrint,
  addChildWindow,
  startProgress,
  stopProgress
} from "./globalBridge";

const EMPTY_COUNTS: MonitorCounts = {
  count_all: 0,
  triage: 0,
  track_and_trend: 0,
  need_review: 0,
  active_tasks: 0,
  patient_statuses: {},
  severities: {}
};

function responseError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "Request failed";
}

function patchQueryState(
  setQueryState: React.Dispatch<React.SetStateAction<MonitorQueryState>>,
  patch: Partial<MonitorQueryState>,
  options?: { resetPage?: boolean }
) {
  setQueryState((prevState) => {
    const nextState = { ...prevState, ...patch };
    if (options && options.resetPage) {
      nextState.page = 1;
    }
    return nextState;
  });
}

const MonitorDashboardApp: React.FC<MonitorAppProps> = (props) => {
  const initialState = useMemo(() => defaultQueryState(props.default_sort, props.default_sort_order, props.meta), [props.default_sort, props.default_sort_order, props.meta]);
  // PR #398 review — sanitize a persisted patientStatuses selection against
  // what's actually offered for this tenant right now (props.meta is the
  // fresh, server-computed meta for this page load), so a stale value can
  // never silently keep filtering the backend query after a tenant config
  // change makes it unavailable (see loadQueryState's own comment in state.ts).
  const [queryState, setQueryState] = useState<MonitorQueryState>(() =>
    loadQueryState(props.state_key, initialState, buildPatientStatusOptions(props.meta).map((option) => option.value))
  );
  const [meta, setMeta] = useState<MonitorMeta>(props.meta);
  const [rows, setRows] = useState<MonitorRow[]>([]);
  const [counts, setCounts] = useState<MonitorCounts>(EMPTY_COUNTS);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const busyAction = false;
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  // HHMCOVID-6327 AC3 (amended 2026-07-31) — restore the previously-focused
  // row on return only if its lock survived the trip (else prod's default:
  // nothing selected). Starting empty avoids flashing a candidate's "Loading
  // details" before the lock check below (isInitialRestoreRef) confirms it.
  const persistedSelection = useMemo(() => loadSelection(props.state_key), [props.state_key]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [focusedPatientId, setFocusedPatientId] = useState<number | null>(null);
  const [quickSearchVisible, setQuickSearchVisible] = useState<boolean>(false);
  const [patientInfoLookup, setPatientInfoLookup] = useState<Record<string, any>>({});
  const [reloadCounter, setReloadCounter] = useState<number>(0);
  // HHMCOVID-6327 AC5 — true only after the first fetch completes (unlike
  // `loading`, which starts false before any fetch begins), so Grid's
  // one-time viewport-fill measurement waits for real data on screen.
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  // HHMCOVID-5632 (BR-019) — per-user hidden columns (seeded from persisted prefs
  // in meta) and the "Customize Columns" dialog open state.
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => props.meta.hidden_columns || []);
  const [columnDialogOpen, setColumnDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    setNavigationMenu("monitor-patients-menu-item");
  }, []);

  useEffect(() => {
    publishSelectedIds(selectedIds);
  }, [selectedIds]);

  // HHMCOVID-6327 — flips to false after the first post-mount fetch; see the
  // reconciliation effect below for what this gates.
  const isInitialRestoreRef = React.useRef<boolean>(true);

  const previousFocusedPatientId = React.useRef<number | null>(null);
  useEffect(() => {
    if (previousFocusedPatientId.current != null && focusedPatientId == null) {
      releaseLocks();
    }
    previousFocusedPatientId.current = focusedPatientId;
  }, [focusedPatientId]);

  useEffect(() => {
    saveQueryState(props.state_key, queryState);
  }, [props.state_key, queryState]);

  // HHMCOVID-6327 AC3 — persist selection/focus so it can be restored above.
  // Skipped while the initial restore decision hasn't landed yet: selectedIds/
  // focusedPatientId start empty and this effect would otherwise persist that
  // empty state immediately on mount, overwriting the real candidate the
  // reconciliation effect still needs to read if a refresh/remount happens
  // before the first fetch resolves. isInitialRestoreRef flips to false in
  // the same batch that settles the real values, which also changes
  // selectedIds/focusedPatientId — so this effect naturally re-runs right
  // after with the settled state; nothing is lost by skipping the first run.
  useEffect(() => {
    if (isInitialRestoreRef.current) {
      return;
    }
    saveSelection(props.state_key, { selectedIds, focusedPatientId });
  }, [props.state_key, selectedIds, focusedPatientId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    startProgress((props.meta.grid_strings && props.meta.grid_strings.loading) || "Loading...");

    fetchMonitorDashboardData(props.data_url, queryState, props.meta)
      .then((response) => {
        if (cancelled) {
          return;
        }

        // HHMCOVID-6234 — batch into a single commit. React 17 does not auto-batch
        // setState inside a promise callback, so without this setPatientInfoLookup could
        // commit (and fire the setCurrentPatient effect below) before focusedPatientId is
        // updated — running the effect with a stale focused id that is no longer in the
        // lookup, which called the legacy setCurrentPatient with null info (crash on the
        // clear-hold reload, leaving the grid blank and the spinner stuck).
        ReactDOM.unstable_batchedUpdates(() => {
          setRows(response.rows || []);
          setCounts(response.counts || EMPTY_COUNTS);
          setTotal(response.total || 0);
          setMeta(response.meta || props.meta);
          setPatientInfoLookup(response.patient_info || {});

          if (response.page && response.page !== queryState.page) {
            setQueryState((prevState) => ({ ...prevState, page: response.page }));
          }

          const allowedIds = new Set((response.rows || []).map((row) => row.id));
          // HHMCOVID-6327 (2026-07-31) — on the first fetch, restore the
          // persisted selection only if lock_class (pre-existing, same field
          // the row's lock icon uses) shows it's still locked by this user.
          // Later fetches (paging/filtering) just re-check the live selection
          // against what's still visible instead.
          const isInitialRestore = isInitialRestoreRef.current;
          const reconciledIds = isInitialRestore
            ? new Set(
                (response.rows || [])
                  .filter((row) => row.lock_class === "patient-locked-by-user")
                  .map((row) => row.id)
              )
            : allowedIds;
          isInitialRestoreRef.current = false;

          setSelectedIds((prevIds) => {
            const candidateIds = isInitialRestore ? persistedSelection?.selectedIds || [] : prevIds;
            const nextIds = candidateIds.filter((id) => reconciledIds.has(id));
            setFocusedPatientId((prevCandidateFocused) => {
              if (isInitialRestore) {
                // AC3 — restore only the specific patient that was focused
                // before, never fall back to some other id that merely
                // happens to also pass the lock check (e.g. a stale
                // multi-select entry) — that's not "restore," and would
                // silently focus/lock a patient the user never asked for.
                const persistedFocused = persistedSelection?.focusedPatientId ?? null;
                return persistedFocused != null && reconciledIds.has(persistedFocused) ? persistedFocused : null;
              }
              if (prevCandidateFocused != null && reconciledIds.has(prevCandidateFocused)) {
                return prevCandidateFocused;
              }
              return nextIds.length > 0 ? nextIds[0] : null;
            });
            return nextIds;
          });
        });
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(responseError(error));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setHasLoadedOnce(true);
          stopProgress();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [props.data_url, props.meta, queryState, reloadCounter, persistedSelection]);

  // HHMCOVID-6327 — true once this mount has told legacy about a real focus.
  // Guards the "nothing selected" branch below: legacy's setCurrentPatient
  // unconditionally releases the lock whenever called with null, even if a
  // dashboard-cache read (still stale right after a lock was just
  // (re)acquired elsewhere) is the reason nothing got restored — that must
  // not destroy a lock legacy's own state still correctly holds.
  const hasSyncedRealFocusRef = React.useRef<boolean>(false);

  useEffect(() => {
    const setCurrentPatientFn = (window as any).setCurrentPatient;
    if (typeof setCurrentPatientFn !== "function") {
      return;
    }

    // Wait for the reconciliation effect's restore decision before syncing
    // anything — it flips isInitialRestoreRef in the same batch that settles
    // selectedIds/focusedPatientId, so this effect re-runs right after with
    // the real answer.
    if (isInitialRestoreRef.current) {
      return;
    }

    if (selectedIds.length === 0 || focusedPatientId == null) {
      if (hasSyncedRealFocusRef.current) {
        setCurrentPatientFn(null, null);
      }
      return;
    }

    const lookupKey = String(focusedPatientId);
    const patientInfo = patientInfoLookup[lookupKey] || null;
    // HHMCOVID-6234 — legacy dereferences options.id, so it can't be called
    // with (id, null). Skip until patientInfo lands rather than crash.
    if (patientInfo == null) {
      return;
    }
    hasSyncedRealFocusRef.current = true;
    setCurrentPatientFn(focusedPatientId, patientInfo);
  }, [focusedPatientId, patientInfoLookup, selectedIds]);

  const filterOptions = useMemo(() => buildFilterOptions(meta), [meta]);
  const patientStatusOptions = useMemo(() => buildPatientStatusOptions(meta), [meta]);

  const onPatchQuery = (patch: Partial<MonitorQueryState>, options?: { resetPage?: boolean }) => {
    patchQueryState(setQueryState, patch, options);
  };

  const lastClickedIdRef = React.useRef<number | null>(null);
  const onSelectRow = (id: number, options?: { multi?: boolean; range?: boolean }) => {
    const multi = !!(options && options.multi);
    const range = !!(options && options.range);
    setSelectedIds((prevIds) => {
      let nextIds: number[];
      if (range && lastClickedIdRef.current != null) {
        const ids = rows.map((row) => row.id);
        const fromIdx = ids.indexOf(lastClickedIdRef.current);
        const toIdx = ids.indexOf(id);
        if (fromIdx === -1 || toIdx === -1) {
          nextIds = [id];
        } else {
          const [start, end] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
          const rangeIds = ids.slice(start, end + 1);
          nextIds = multi ? Array.from(new Set(prevIds.concat(rangeIds))) : rangeIds;
        }
      } else if (multi) {
        nextIds = prevIds.includes(id) ? prevIds.filter((savedId) => savedId !== id) : prevIds.concat([id]);
      } else {
        nextIds = [id];
      }

      setFocusedPatientId(nextIds.includes(id) ? id : (nextIds.length > 0 ? nextIds[nextIds.length - 1] : null));
      return nextIds;
    });
    lastClickedIdRef.current = id;
  };

  const refreshData = () => {
    setReloadCounter((count) => count + 1);
  };

  useEffect(() => {
    publishFilterChangeHandlers({
      onTreatmentTeamChange: (value: string) => {
        setQueryState((prev) => ({ ...prev, treatment_team: value, page: 1 }));
      },
      onCareManagerChange: () => {
        const el = document.getElementById("filter[care_manager]") as HTMLSelectElement | null;
        const value = el ? el.value : "";
        setQueryState((prev) => ({ ...prev, care_manager: value, page: 1 }));
      }
    });
  }, []);

  useEffect(() => {
    publishReloadCallback(refreshData);
    // HHMCOVID-6234 — the lock payload (Patient.list_locks) is the COMPLETE set of
    // patients currently holding any lock/hold, so a row absent from it holds no lock.
    // Mirror legacy refreshLocks(): clear every row's lock state, then re-apply from
    // the payload. Previously rows absent from the payload were left untouched, so a
    // released patient (which drops out of the payload when you switch patients) kept
    // its stale "locked by you" icon and locks appeared to accumulate.
    publishLockPatcher((payload) => {
      if (!Array.isArray(payload)) {
        return;
      }
      const lockById = new Map((payload as Array<any>).map((entry) => [Number(entry.id), entry]));
      setRows((prevRows) =>
        prevRows.map((row) => {
          const match = lockById.get(row.id);
          return {
            ...row,
            lock_class: match ? match.locked_class || "" : "",
            lock_help: match ? match.locked_help || "" : ""
          };
        })
      );
    });
  }, []);

  useEffect(() => {
    const printerFriendly = () => {
      const params = buildDashboardParams(queryState);
      const printUrl = meta.print_url + (meta.print_url.includes("?") ? "&" : "?") + params.toString();
      const win = window.open(printUrl);
      addChildWindow(win);
    };
    enablePrint(printerFriendly);
  }, [meta.print_url, queryState]);


  const openPatient = (patientId: number) => {
    startProgress((meta.grid_strings && meta.grid_strings.loading) || "Loading...");
    const patientUrl = meta.patient_summary_url_template.replace("__PATIENT_ID__", String(patientId));
    window.location.assign(patientUrl);
  };

  const onSearch = (payload: { qtype: string; query: string; qdesc: string }) => {
    onPatchQuery(
      {
        qtype: payload.qtype,
        query: payload.query.trim(),
        qdesc: payload.qdesc
      },
      { resetPage: true }
    );
  };

  const onClearSearch = () => {
    onPatchQuery(
      {
        query: "",
        qdesc: ""
      },
      { resetPage: true }
    );
  };

  const onSort = (nextSortname: string, initialOrder: SortOrder = "asc") => {
    if (!nextSortname) {
      return;
    }

    // Same column → toggle; new column → its configured first-click direction
    // (severity/vital columns default to "desc" so worst readings surface first — HHMCOVID-5607).
    const nextSortorder =
      queryState.sortname === nextSortname
        ? queryState.sortorder === "asc" ? "desc" : "asc"
        : initialOrder;
    onPatchQuery({ sortname: nextSortname, sortorder: nextSortorder }, { resetPage: true });
  };

  // HHMCOVID-5632 (BR-019) — build the full ordered column list once here, shared
  // by the Grid (which filters out hidden columns) and the "Customize Columns"
  // dialog (which lists every column with its required flag).
  const allColumns = useMemo(() => {
    const headers = meta.column_headers || ({} as any);
    const noneText = (meta as any).status_labels?.none || "None";
    const patientStatusLabels: Record<string, string> = meta.patient_status_labels || {};
    return buildMonitorColumns({ meta, headers, noneText, patientStatusLabels });
  }, [meta]);

  const onChangeColumnVisibility = (nextHidden: string[]) => {
    const previousHidden = hiddenColumns;
    setHiddenColumns(nextHidden); // optimistic — immediate show/hide, no refetch
    postColumnVisibility(meta.save_column_visibility_url, nextHidden)
      .then((response) => {
        if (response && Array.isArray(response.hidden_columns)) {
          setHiddenColumns(response.hidden_columns); // reconcile to server truth
        }
      })
      .catch((error) => {
        setHiddenColumns(previousHidden); // revert on failure
        setErrorMessage(responseError(error));
      });
  };

  const filterMount = typeof document !== "undefined" ? document.getElementById("monitor-react-filter-mount") : null;
  // HHMCOVID-5632 (BR-019) — the "Customize Columns" trigger is portaled into the
  // header controls row, next to the "Patients" dropdown, and styled to match it
  // (Bootstrap btn btn-secondary — the same grey palette).
  const customizeMount = typeof document !== "undefined" ? document.getElementById("monitor-react-customize-columns-mount") : null;
  const customizeColumnsButton = meta.can_manage_column_visibility ? (
    <button
      type="button"
      className="btn btn-secondary"
      aria-haspopup="dialog"
      onClick={() => setColumnDialogOpen(true)}
    >
      {meta.column_visibility_labels.button}
    </button>
  ) : null;
  const filterBar = (
    <FilterBar
      meta={meta}
      counts={counts}
      queryState={queryState}
      filterOptions={filterOptions}
      patientStatusOptions={patientStatusOptions}
      onPatchQuery={onPatchQuery}
    />
  );

  return (
    <div className="monitor-dashboard-react-app">
      {filterMount ? ReactDOM.createPortal(filterBar, filterMount) : filterBar}
      {customizeMount && customizeColumnsButton ? ReactDOM.createPortal(customizeColumnsButton, customizeMount) : null}

      {errorMessage && <div className="alert alert-danger py-2">{errorMessage}</div>}
      {successMessage && <div className="alert alert-success py-2">{successMessage}</div>}

      <Grid
        rows={rows}
        meta={meta}
        columns={allColumns}
        hiddenColumns={hiddenColumns}
        selectedIds={selectedIds}
        focusedPatientId={focusedPatientId}
        sortname={queryState.sortname}
        sortorder={queryState.sortorder}
        loading={loading}
        hasLoadedOnce={hasLoadedOnce}
        onSelectRow={onSelectRow}
        onSort={onSort}
        onOpenPatient={openPatient}
      />

      <div className="flexigrid" style={{ width: "100%" }}>
        <QuickSearchBar
          searchItems={meta.search_items || []}
          qtype={queryState.qtype}
          query={queryState.query}
          busy={loading || busyAction}
          visible={quickSearchVisible}
          gridStrings={meta.grid_strings}
          onSearch={onSearch}
          onClear={onClearSearch}
        />

        <Pagination
          page={queryState.page}
          rp={queryState.rp}
          total={total}
          rowCount={rows.length}
          gridStrings={meta.grid_strings}
          loading={loading}
          searchVisible={quickSearchVisible}
          onPageChange={(nextPage) => onPatchQuery({ page: nextPage })}
          onPageSizeChange={(nextPageSize) => onPatchQuery({ rp: nextPageSize }, { resetPage: true })}
          onRefresh={refreshData}
          onToggleSearch={() => setQuickSearchVisible((v) => !v)}
        />
      </div>

      <PatientDetailsPanel
        patientId={focusedPatientId}
        urlTemplate={meta.current_readings_url_template}
        loadingMessage={(() => {
          const tmpl = meta.grid_strings?.loading_details || "";
          if (!tmpl || focusedPatientId == null) {
            return "";
          }
          const info = patientInfoLookup[String(focusedPatientId)];
          const name = (info && (info.patientName || info.name)) || "";
          return tmpl.replace("{name}", name);
        })()}
      />

      {meta.can_manage_column_visibility && (
        <CustomizeColumnsDialog
          open={columnDialogOpen}
          columns={allColumns}
          hiddenColumns={hiddenColumns}
          labels={meta.column_visibility_labels}
          onClose={() => setColumnDialogOpen(false)}
          onChange={onChangeColumnVisibility}
        />
      )}
    </div>
  );
};

export default MonitorDashboardApp;
